import React, { useEffect, useState, useCallback } from 'react';
import { supabaseAdmin } from '../config/supabase';
import toast from 'react-hot-toast';
import './ChatAssistantManagement.css';

const MAX_PINNED_FAQ = 5;

const DEFAULT_WELCOME =
  'Cześć! Jestem Bappcią, wirtualnym asystentem. Mogę odpowiedzieć na podstawie oficjalnych materiałów i FAQ. ' +
  'To nie zastępuje kontaktu z urzędem w sprawach indywidualnych.';

/** Normalizacja listy UUID z kolumny JSONB pinned_faq_ids (max MAX_PINNED_FAQ, kolejność zachowana). */
function normalizePinnedFaqIds(raw) {
  if (raw == null) return [];
  let arr = raw;
  if (typeof raw === 'string') {
    try {
      arr = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(arr)) return [];
  const out = [];
  const seen = new Set();
  for (const x of arr) {
    const s = String(x).trim();
    if (!s || seen.has(s)) continue;
    seen.add(s);
    out.push(s);
    if (out.length >= MAX_PINNED_FAQ) break;
  }
  return out;
}

/** Opcje rodzica w select — BFS, bez cykli: wyklucz węzeł edytowany i jego potomków. */
function intentParentOptions(nodes, excludeId) {
  const byParent = new Map();
  for (const n of nodes) {
    const p = n.parent_id;
    if (!byParent.has(p)) byParent.set(p, []);
    byParent.get(p).push(n);
  }
  for (const arr of byParent.values()) {
    arr.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }
  const forbidden = new Set();
  if (excludeId) {
    forbidden.add(excludeId);
    const stack = [...(byParent.get(excludeId) || [])];
    while (stack.length) {
      const n = stack.pop();
      forbidden.add(n.id);
      for (const c of byParent.get(n.id) || []) stack.push(c);
    }
  }
  const out = [{ value: '', label: '— korzeń drzewa (bez rodzica) —' }];
  function walk(parentId, depth) {
    for (const n of byParent.get(parentId) || []) {
      if (forbidden.has(n.id)) continue;
      const prefix = depth > 0 ? `${'\u00A0\u00A0'.repeat(Math.min(depth, 6))}↳ ` : '';
      out.push({ value: n.id, label: `${prefix}${n.label}` });
      walk(n.id, depth + 1);
    }
  }
  walk(null, 0);
  return out;
}

/** Płaska lista do tabeli — zachowuje kolejność drzewa. */
function flattenIntentNodesForTable(nodes) {
  const byParent = new Map();
  for (const n of nodes) {
    const p = n.parent_id;
    if (!byParent.has(p)) byParent.set(p, []);
    byParent.get(p).push(n);
  }
  for (const arr of byParent.values()) {
    arr.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }
  const rows = [];
  function walk(parentId, depth) {
    for (const n of byParent.get(parentId) || []) {
      rows.push({ node: n, depth });
      walk(n.id, depth + 1);
    }
  }
  walk(null, 0);
  return rows;
}

export default function ChatAssistantManagement() {
  const [loading, setLoading] = useState(true);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [savingWelcome, setSavingWelcome] = useState(false);
  const [crawlBaseUrl, setCrawlBaseUrl] = useState('');
  const [maxPages, setMaxPages] = useState(40);
  const [savingCrawlSettings, setSavingCrawlSettings] = useState(false);
  /** false = wyłączony czat w aplikacji mobilnej (kolumna assistant_chat_enabled). */
  const [assistantChatEnabled, setAssistantChatEnabled] = useState(true);
  const [assistantChatFlagDbReady, setAssistantChatFlagDbReady] = useState(true);
  const [savingAssistantChatFlag, setSavingAssistantChatFlag] = useState(false);
  const [faqs, setFaqs] = useState([]);
  /** ID przypiętych FAQ — zapis w chat_bot_settings.pinned_faq_ids (JSON). */
  const [pinnedFaqIds, setPinnedFaqIds] = useState([]);
  /** false = brak kolumny pinned_faq_ids w bazie (migracja nie wykonana). */
  const [pinnedFaqDbReady, setPinnedFaqDbReady] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formQ, setFormQ] = useState('');
  const [formA, setFormA] = useState('');
  const [formOrder, setFormOrder] = useState(0);
  const [formActive, setFormActive] = useState(true);
  /** Zapisywanie przypięcia — blokada podwójnego kliku przy jednym wierszu. */
  const [pinSavingId, setPinSavingId] = useState(null);

  const [intentNodes, setIntentNodes] = useState([]);
  const [intentTableReady, setIntentTableReady] = useState(true);
  const [intentParentId, setIntentParentId] = useState('');
  const [intentLabel, setIntentLabel] = useState('');
  const [intentKeywords, setIntentKeywords] = useState('');
  /** Gotowa odpowiedź po dojściu do tego kafelka (liść) — kolumna canned_response. */
  const [intentCanned, setIntentCanned] = useState('');
  const [intentSort, setIntentSort] = useState(0);
  const [intentActive, setIntentActive] = useState(true);
  const [editingIntent, setEditingIntent] = useState(null);
  const [savingIntent, setSavingIntent] = useState(false);

  /** Trwałe korekty dla Bappci (tabela chat_assistant_corrections). */
  const [corrections, setCorrections] = useState([]);
  const [correctionsTableReady, setCorrectionsTableReady] = useState(true);
  const [editingCorrection, setEditingCorrection] = useState(null);
  const [correctionTitle, setCorrectionTitle] = useState('');
  const [correctionBody, setCorrectionBody] = useState('');
  const [correctionSort, setCorrectionSort] = useState(0);
  const [correctionActive, setCorrectionActive] = useState(true);
  const [savingCorrection, setSavingCorrection] = useState(false);

  /** Feedback własnymi słowami (jak w Cursorze) — tabela chat_assistant_feedback. */
  const [feedbackEntries, setFeedbackEntries] = useState([]);
  const [feedbackTableReady, setFeedbackTableReady] = useState(true);
  const [editingFeedback, setEditingFeedback] = useState(null);
  const [fbAdminNote, setFbAdminNote] = useState('');
  const [fbUserQuestion, setFbUserQuestion] = useState('');
  const [fbWrongAnswer, setFbWrongAnswer] = useState('');
  const [fbActive, setFbActive] = useState(true);
  const [savingFeedback, setSavingFeedback] = useState(false);

  const pinnedCount = pinnedFaqIds.length;

  const persistPinnedFaqIds = useCallback(
    async (nextRaw) => {
      const unique = normalizePinnedFaqIds(nextRaw);
      const now = new Date().toISOString();
      const { data: updated, error } = await supabaseAdmin
        .from('chat_bot_settings')
        .update({ pinned_faq_ids: unique, updated_at: now })
        .eq('id', 1)
        .select('id');
      if (error) throw error;
      if (!updated?.length) {
        const { error: insErr } = await supabaseAdmin.from('chat_bot_settings').insert({
          id: 1,
          max_pages: Math.min(2000, Math.max(1, Number(maxPages) || 40)),
          crawl_base_url: crawlBaseUrl.trim() || null,
          assistant_welcome_message: welcomeMessage.trim() || DEFAULT_WELCOME,
          pinned_faq_ids: unique,
          updated_at: now,
        });
        if (insErr) throw insErr;
      }
      setPinnedFaqIds(unique);
    },
    [welcomeMessage, crawlBaseUrl, maxPages],
  );

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);

      let setData = null;
      let pinIds = [];
      let pinsOk = true;
      let assistantFlagOk = true;

      let s1 = await supabaseAdmin
        .from('chat_bot_settings')
        .select('assistant_welcome_message, crawl_base_url, max_pages, pinned_faq_ids, assistant_chat_enabled')
        .eq('id', 1)
        .maybeSingle();

      if (s1.error) {
        assistantFlagOk = false;
        s1 = await supabaseAdmin
          .from('chat_bot_settings')
          .select('assistant_welcome_message, crawl_base_url, max_pages, pinned_faq_ids')
          .eq('id', 1)
          .maybeSingle();
      }
      if (s1.error) {
        pinsOk = false;
        assistantFlagOk = false;
        s1 = await supabaseAdmin
          .from('chat_bot_settings')
          .select('assistant_welcome_message, crawl_base_url, max_pages')
          .eq('id', 1)
          .maybeSingle();
      }
      if (s1.error) throw s1.error;

      setData = s1.data;
      pinIds = normalizePinnedFaqIds(s1.data?.pinned_faq_ids);
      if (assistantFlagOk && s1.data && 'assistant_chat_enabled' in s1.data) {
        setAssistantChatEnabled(s1.data.assistant_chat_enabled !== false);
      } else {
        setAssistantChatEnabled(true);
      }

      setAssistantChatFlagDbReady(assistantFlagOk);
      setPinnedFaqDbReady(pinsOk);
      setPinnedFaqIds(pinIds);

      const w = (setData?.assistant_welcome_message ?? '').trim();
      setWelcomeMessage(w || DEFAULT_WELCOME);
      setCrawlBaseUrl((setData?.crawl_base_url ?? '').trim());
      setMaxPages(Number(setData?.max_pages) > 0 ? Number(setData.max_pages) : 40);

      const faqRes = await supabaseAdmin
        .from('chat_faq')
        .select('id, question, answer, sort_order, is_active, created_at, updated_at')
        .order('sort_order', { ascending: true });
      if (faqRes.error) throw faqRes.error;
      setFaqs(faqRes.data || []);

      try {
        let ir = await supabaseAdmin
          .from('chat_intent_nodes')
          .select('id, parent_id, label, retrieval_keywords, sort_order, is_active, created_at, canned_response')
          .order('sort_order', { ascending: true });
        if (ir.error && /canned_response|column|42703/i.test(String(ir.error.message || ''))) {
          ir = await supabaseAdmin
            .from('chat_intent_nodes')
            .select('id, parent_id, label, retrieval_keywords, sort_order, is_active, created_at')
            .order('sort_order', { ascending: true });
        }
        if (ir.error) throw ir.error;
        setIntentNodes(ir.data || []);
        setIntentTableReady(true);
      } catch (ie) {
        const em = String(ie.message || ie.details || ie.code || '');
        if (/does not exist|42P01|chat_intent_nodes|42703|schema cache/i.test(em)) {
          setIntentNodes([]);
          setIntentTableReady(false);
        } else {
          console.error(ie);
          setIntentNodes([]);
          setIntentTableReady(true);
        }
      }

      try {
        const cr = await supabaseAdmin
          .from('chat_assistant_corrections')
          .select('id, title, body, sort_order, is_active, created_at, updated_at')
          .order('sort_order', { ascending: true });
        if (cr.error) throw cr.error;
        setCorrections(cr.data || []);
        setCorrectionsTableReady(true);
      } catch (ce) {
        const em = String(ce.message || ce.details || '');
        if (/does not exist|42P01|chat_assistant_corrections|42703|schema cache/i.test(em)) {
          setCorrections([]);
          setCorrectionsTableReady(false);
        } else {
          console.error(ce);
          setCorrections([]);
          setCorrectionsTableReady(true);
        }
      }

      try {
        const fr = await supabaseAdmin
          .from('chat_assistant_feedback')
          .select('id, admin_note, user_question_excerpt, wrong_answer_excerpt, is_active, created_at, updated_at')
          .order('created_at', { ascending: false });
        if (fr.error) throw fr.error;
        setFeedbackEntries(fr.data || []);
        setFeedbackTableReady(true);
      } catch (fe) {
        const em = String(fe.message || fe.details || '');
        if (/does not exist|42P01|chat_assistant_feedback|42703|schema cache/i.test(em)) {
          setFeedbackEntries([]);
          setFeedbackTableReady(false);
        } else {
          console.error(fe);
          setFeedbackEntries([]);
          setFeedbackTableReady(true);
        }
      }
    } catch (e) {
      console.error(e);
      toast.error(
        'Nie udało się wczytać danych. Uruchom migracje SQL (chat_assistant, pinned_faq_ids w chat_bot_settings).',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const resetForm = () => {
    setEditing(null);
    setFormQ('');
    setFormA('');
    setFormOrder(0);
    setFormActive(true);
  };

  const startEdit = (row) => {
    setEditing(row);
    setFormQ(row.question);
    setFormA(row.answer);
    setFormOrder(row.sort_order ?? 0);
    setFormActive(row.is_active !== false);
  };

  const saveWelcome = async () => {
    const text = welcomeMessage.trim();
    if (!text) {
      toast.error('Tekst powitalny nie może być pusty');
      return;
    }
    try {
      setSavingWelcome(true);
      const now = new Date().toISOString();
      const { data: updated, error: updErr } = await supabaseAdmin
        .from('chat_bot_settings')
        .update({
          assistant_welcome_message: text,
          updated_at: now,
        })
        .eq('id', 1)
        .select('id');
      if (updErr) throw updErr;
      if (!updated?.length) {
        const insertRow = {
          id: 1,
          max_pages: 40,
          assistant_welcome_message: text,
          updated_at: now,
        };
        if (pinnedFaqDbReady) insertRow.pinned_faq_ids = pinnedFaqIds;
        const { error: insErr } = await supabaseAdmin.from('chat_bot_settings').insert(insertRow);
        if (insErr) throw insErr;
      }
      toast.success('Zapisano tekst powitalny');
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Błąd zapisu');
    } finally {
      setSavingWelcome(false);
    }
  };

  const toggleAssistantChatInApp = async (checked) => {
    if (!assistantChatFlagDbReady) return;
    try {
      setSavingAssistantChatFlag(true);
      const now = new Date().toISOString();
      const { data: updated, error: updErr } = await supabaseAdmin
        .from('chat_bot_settings')
        .update({ assistant_chat_enabled: checked, updated_at: now })
        .eq('id', 1)
        .select('id');
      if (updErr) throw updErr;
      if (!updated?.length) {
        const insertRow = {
          id: 1,
          max_pages: Math.min(2000, Math.max(1, Number(maxPages) || 40)),
          crawl_base_url: crawlBaseUrl.trim() || null,
          assistant_welcome_message: welcomeMessage.trim() || DEFAULT_WELCOME,
          assistant_chat_enabled: checked,
          updated_at: now,
        };
        if (pinnedFaqDbReady) insertRow.pinned_faq_ids = pinnedFaqIds;
        const { error: insErr } = await supabaseAdmin.from('chat_bot_settings').insert(insertRow);
        if (insErr) throw insErr;
      }
      setAssistantChatEnabled(checked);
      toast.success(
        checked ? 'Czat Bappci włączony w aplikacji mobilnej' : 'Czat Bappci wyłączony w aplikacji mobilnej',
      );
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Błąd zapisu');
    } finally {
      setSavingAssistantChatFlag(false);
    }
  };

  const saveCrawlSettings = async () => {
    const urlRaw = crawlBaseUrl.trim();
    if (urlRaw) {
      try {
        const u = new URL(urlRaw);
        if (u.protocol !== 'http:' && u.protocol !== 'https:') {
          toast.error('Adres musi zaczynać się od http:// lub https://');
          return;
        }
      } catch {
        toast.error('Nieprawidłowy adres URL');
        return;
      }
    }
    const mp = Math.min(2000, Math.max(1, Number(maxPages) || 40));
    try {
      setSavingCrawlSettings(true);
      const now = new Date().toISOString();
      const { data: updated, error: updErr } = await supabaseAdmin
        .from('chat_bot_settings')
        .update({
          crawl_base_url: urlRaw || null,
          max_pages: mp,
          updated_at: now,
        })
        .eq('id', 1)
        .select('id');
      if (updErr) throw updErr;
      if (!updated?.length) {
        const insertRow = {
          id: 1,
          max_pages: mp,
          crawl_base_url: urlRaw || null,
          assistant_welcome_message: welcomeMessage.trim() || DEFAULT_WELCOME,
          updated_at: now,
        };
        if (pinnedFaqDbReady) insertRow.pinned_faq_ids = pinnedFaqIds;
        const { error: insErr } = await supabaseAdmin.from('chat_bot_settings').insert(insertRow);
        if (insErr) throw insErr;
      }
      setMaxPages(mp);
      setCrawlBaseUrl(urlRaw);
      toast.success('Zapisano ustawienia indeksu BIP (chat_bot_settings)');
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Błąd zapisu');
    } finally {
      setSavingCrawlSettings(false);
    }
  };

  const saveFaq = async () => {
    const q = formQ.trim();
    const a = formA.trim();
    if (!q || !a) {
      toast.error('Wypełnij pytanie i odpowiedź');
      return;
    }
    try {
      if (editing) {
        const { error } = await supabaseAdmin
          .from('chat_faq')
          .update({
            question: q,
            answer: a,
            sort_order: Number(formOrder) || 0,
            is_active: formActive,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editing.id);
        if (error) throw error;
        toast.success('Zapisano wpis FAQ');
      } else {
        const { error } = await supabaseAdmin.from('chat_faq').insert({
          question: q,
          answer: a,
          sort_order: Number(formOrder) || 0,
          is_active: formActive,
        });
        if (error) throw error;
        toast.success('Dodano wpis FAQ');
      }
      resetForm();
      loadAll();
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Błąd zapisu');
    }
  };

  /** Zaznaczenie = przypnij (tylko aktywne FAQ); odznaczenie = zawsze dozwolone. */
  const setFaqPinned = async (row, checked) => {
    if (!pinnedFaqDbReady) {
      toast.error(
        'Brak kolumny pinned_faq_ids. W Supabase SQL Editor uruchom migrację: supabase/migrations/20260331203000_chat_bot_settings_pinned_faq_ids.sql',
      );
      return;
    }
    const id = String(row.id);
    const currentlyPinned = pinnedFaqIds.includes(id);
    if (checked === currentlyPinned) return;

    if (checked) {
      if (!row.is_active) {
        toast.error('Aktywuj wpis FAQ, potem zaznacz kafelek w czacie.');
        return;
      }
      if (pinnedFaqIds.length >= MAX_PINNED_FAQ) {
        toast.error(`Maksymalnie ${MAX_PINNED_FAQ} kafelków — odznacz któryś inny wpis.`);
        return;
      }
    }

    const next = checked
      ? [...pinnedFaqIds, id]
      : pinnedFaqIds.filter((x) => x !== id);

    try {
      setPinSavingId(row.id);
      await persistPinnedFaqIds(next);
      toast.success(checked ? 'Zapisano — kafelek pojawi się w czacie mobilnym' : 'Usunięto z kafelków w czacie');
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Błąd zapisu przypięć');
    } finally {
      setPinSavingId(null);
    }
  };

  const resetIntentForm = () => {
    setEditingIntent(null);
    setIntentParentId('');
    setIntentLabel('');
    setIntentKeywords('');
    setIntentCanned('');
    setIntentSort(0);
    setIntentActive(true);
  };

  const startEditIntent = (row) => {
    setEditingIntent(row);
    setIntentParentId(row.parent_id ? String(row.parent_id) : '');
    setIntentLabel(row.label || '');
    setIntentKeywords(row.retrieval_keywords || '');
    setIntentCanned(row.canned_response || '');
    setIntentSort(Number(row.sort_order) || 0);
    setIntentActive(row.is_active !== false);
  };

  const saveIntent = async () => {
    const label = intentLabel.trim();
    if (!label) {
      toast.error('Podaj etykietę kafelka');
      return;
    }
    if (!intentTableReady) {
      toast.error('Uruchom migrację SQL: chat_intent_nodes (supabase/migrations/20260403120000_chat_intent_nodes.sql)');
      return;
    }
    try {
      setSavingIntent(true);
      const now = new Date().toISOString();
      const row = {
        parent_id: intentParentId ? intentParentId.trim() : null,
        label,
        retrieval_keywords: intentKeywords.trim() || null,
        canned_response: intentCanned.trim() || null,
        sort_order: Number(intentSort) || 0,
        is_active: intentActive,
        updated_at: now,
      };
      if (editingIntent) {
        const { error } = await supabaseAdmin.from('chat_intent_nodes').update(row).eq('id', editingIntent.id);
        if (error) throw error;
        toast.success('Zapisano węzeł drzewa');
      } else {
        const { error } = await supabaseAdmin.from('chat_intent_nodes').insert(row);
        if (error) throw error;
        toast.success('Dodano węzeł');
      }
      resetIntentForm();
      loadAll();
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Błąd zapisu');
    } finally {
      setSavingIntent(false);
    }
  };

  const deleteIntent = async (id) => {
    if (!window.confirm('Usunąć ten węzeł? (Usunięte zostaną też węzły podrzędne.)')) return;
    try {
      const { error } = await supabaseAdmin.from('chat_intent_nodes').delete().eq('id', id);
      if (error) throw error;
      if (editingIntent?.id === id) resetIntentForm();
      toast.success('Usunięto');
      loadAll();
    } catch (e) {
      toast.error(e.message || 'Błąd usuwania');
    }
  };

  const resetFeedbackForm = () => {
    setEditingFeedback(null);
    setFbAdminNote('');
    setFbUserQuestion('');
    setFbWrongAnswer('');
    setFbActive(true);
  };

  const startEditFeedback = (row) => {
    setEditingFeedback(row);
    setFbAdminNote(row.admin_note || '');
    setFbUserQuestion(row.user_question_excerpt || '');
    setFbWrongAnswer(row.wrong_answer_excerpt || '');
    setFbActive(row.is_active !== false);
  };

  const saveFeedback = async () => {
    const note = fbAdminNote.trim();
    if (!note) {
      toast.error('Napisz swoją uwagę — pole nie może być puste');
      return;
    }
    if (!feedbackTableReady) {
      toast.error('Uruchom migrację SQL: chat_assistant_feedback (plik w supabase/migrations).');
      return;
    }
    try {
      setSavingFeedback(true);
      const now = new Date().toISOString();
      const row = {
        admin_note: note,
        user_question_excerpt: fbUserQuestion.trim() || null,
        wrong_answer_excerpt: fbWrongAnswer.trim() || null,
        is_active: fbActive,
        updated_at: now,
      };
      if (editingFeedback) {
        const { error } = await supabaseAdmin.from('chat_assistant_feedback').update(row).eq('id', editingFeedback.id);
        if (error) throw error;
        toast.success('Zapisano uwagę');
      } else {
        const { error } = await supabaseAdmin.from('chat_assistant_feedback').insert(row);
        if (error) throw error;
        toast.success('Dodano uwagę — model weźmie ją pod uwagę przy kolejnych odpowiedziach');
      }
      resetFeedbackForm();
      loadAll();
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Błąd zapisu');
    } finally {
      setSavingFeedback(false);
    }
  };

  const deleteFeedback = async (id) => {
    if (!window.confirm('Usunąć ten wpis feedbacku?')) return;
    try {
      const { error } = await supabaseAdmin.from('chat_assistant_feedback').delete().eq('id', id);
      if (error) throw error;
      if (editingFeedback?.id === id) resetFeedbackForm();
      toast.success('Usunięto');
      loadAll();
    } catch (e) {
      toast.error(e.message || 'Błąd usuwania');
    }
  };

  const resetCorrectionForm = () => {
    setEditingCorrection(null);
    setCorrectionTitle('');
    setCorrectionBody('');
    setCorrectionSort(0);
    setCorrectionActive(true);
  };

  const startEditCorrection = (row) => {
    setEditingCorrection(row);
    setCorrectionTitle(row.title || '');
    setCorrectionBody(row.body || '');
    setCorrectionSort(Number(row.sort_order) || 0);
    setCorrectionActive(row.is_active !== false);
  };

  const saveCorrection = async () => {
    const body = correctionBody.trim();
    if (!body) {
      toast.error('Treść korekty nie może być pusta');
      return;
    }
    if (!correctionsTableReady) {
      toast.error('Uruchom migrację SQL: chat_assistant_corrections (plik w supabase/migrations).');
      return;
    }
    try {
      setSavingCorrection(true);
      const now = new Date().toISOString();
      const row = {
        title: correctionTitle.trim() || null,
        body,
        sort_order: Number(correctionSort) || 0,
        is_active: correctionActive,
        updated_at: now,
      };
      if (editingCorrection) {
        const { error } = await supabaseAdmin.from('chat_assistant_corrections').update(row).eq('id', editingCorrection.id);
        if (error) throw error;
        toast.success('Zapisano korektę');
      } else {
        const { error } = await supabaseAdmin.from('chat_assistant_corrections').insert(row);
        if (error) throw error;
        toast.success('Dodano korektę');
      }
      resetCorrectionForm();
      loadAll();
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Błąd zapisu');
    } finally {
      setSavingCorrection(false);
    }
  };

  const deleteCorrection = async (id) => {
    if (!window.confirm('Usunąć tę korektę?')) return;
    try {
      const { error } = await supabaseAdmin.from('chat_assistant_corrections').delete().eq('id', id);
      if (error) throw error;
      if (editingCorrection?.id === id) resetCorrectionForm();
      toast.success('Usunięto');
      loadAll();
    } catch (e) {
      toast.error(e.message || 'Błąd usuwania');
    }
  };

  const deleteFaq = async (id) => {
    if (!window.confirm('Usunąć ten wpis FAQ?')) return;
    const sid = String(id);
    try {
      const { error } = await supabaseAdmin.from('chat_faq').delete().eq('id', id);
      if (error) throw error;
      if (pinnedFaqDbReady && pinnedFaqIds.includes(sid)) {
        await persistPinnedFaqIds(pinnedFaqIds.filter((x) => x !== sid));
      }
      toast.success('Usunięto');
      loadAll();
    } catch (e) {
      toast.error(e.message || 'Błąd usuwania');
    }
  };

  if (loading) {
    return (
      <div className="chat-assistant-admin">
        <p>Ładowanie…</p>
      </div>
    );
  }

  return (
    <div className="chat-assistant-admin">
      <h1>Bappcia — czat w aplikacji</h1>
      <p className="lead">
        <strong>Drzewo tematów (kafelki)</strong> — pierwsza sekcja poniżej: budowa poziomów (korzeń → podtematy) i
        słowa kluczowe do wyszukiwania. Dalej: powitanie, adres BIP (<strong>chat_bot_settings</strong>) oraz FAQ.
        Materiały trafiają do funkcji Edge <strong>chat-assistant</strong>.
      </p>

      <section className="chat-assistant-master-switch" aria-labelledby="chat-master-switch-title">
        <h2 id="chat-master-switch-title">Czat w aplikacji mobilnej</h2>
        <p className="help">
          Gdy wyłączysz poniższą opcję, użytkownicy <strong>nie zobaczą</strong> przycisku czatu (Bappcia) ani innych
          wejść do asystenta — tak jakby zakładka była niewidoczna. Ustawienie zapisuje się w{' '}
          <code>chat_bot_settings.assistant_chat_enabled</code>.
        </p>
        {!assistantChatFlagDbReady && (
          <div className="chat-assistant-migration-alert" role="alert">
            <strong>Brak kolumny wyłącznika.</strong> Uruchom migrację SQL:{' '}
            <code>supabase/migrations/20260430200000_chat_bot_settings_assistant_chat_enabled.sql</code>, potem odśwież
            stronę.
          </div>
        )}
        <label
          className="chat-assistant-master-switch-label"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            cursor: assistantChatFlagDbReady ? 'pointer' : 'not-allowed',
          }}
        >
          <input
            type="checkbox"
            checked={assistantChatEnabled}
            onChange={(e) => toggleAssistantChatInApp(e.target.checked)}
            disabled={!assistantChatFlagDbReady || savingAssistantChatFlag}
            aria-describedby="chat-master-switch-title"
          />
          <span>
            <strong>Włącz czat Bappcia w aplikacji</strong>
            {savingAssistantChatFlag ? (
              <span style={{ display: 'block', marginTop: 6, fontSize: 13, color: '#6b7280' }}>Zapisywanie…</span>
            ) : (
              <span style={{ display: 'block', marginTop: 6, fontWeight: 400, fontSize: 14, color: '#4b5563' }}>
                Zaznaczone: pływający przycisk czatu i dostęp do asystenta są aktywne (przy włączonej zakładce w
                „Zarządzanie zakładkami”).
              </span>
            )}
          </span>
        </label>
      </section>

      {!pinnedFaqDbReady && (
        <div className="chat-assistant-migration-alert" role="alert">
          <strong>Brak kolumny przypięć w ustawieniach.</strong> Kafelki w aplikacji wymagają kolumny{' '}
          <code>chat_bot_settings.pinned_faq_ids</code>. Wykonaj w Supabase SQL: plik{' '}
          <code>supabase/migrations/20260331203000_chat_bot_settings_pinned_faq_ids.sql</code>, potem odśwież tę
          stronę.
        </div>
      )}

      {!intentTableReady && (
        <div className="chat-assistant-migration-alert" role="alert">
          <strong>Brak tabeli drzewa tematów.</strong> Wykonaj migrację{' '}
          <code>supabase/migrations/20260403120000_chat_intent_nodes.sql</code> w Supabase SQL Editor, potem odśwież
          stronę.
        </div>
      )}

      <section>
        <h2>Drzewo tematów (kafelki w czacie)</h2>
        <p className="help">
          Użytkownik może przechodzić przez poziomy: korzeń → podtematy → … — na końcu dostaje odpowiedź z kontekstu BIP
          (albo <strong>gotową treść</strong> z pola poniżej, jeśli wypełnisz ją dla liścia). Pole{' '}
          <strong>Słowa kluczowe</strong> (po przecinku) pomaga dopasować nieprecyzyjne pytania (np. „śmieci kiedy”) i
          wzbogaca wyszukiwanie. Liść drzewa = ostatni poziom bez dzieci — wtedy wysyłane jest zapytanie do AI z pełną
          ścieżką, chyba że ustawisz gotową odpowiedź.
        </p>
        <label htmlFor="intent-parent">Nadrzędny węzeł</label>
        <select
          id="intent-parent"
          value={intentParentId}
          onChange={(e) => setIntentParentId(e.target.value)}
          disabled={!intentTableReady}
          style={{ maxWidth: '100%', marginBottom: 8 }}
        >
          {intentParentOptions(intentNodes, editingIntent?.id).map((o) => (
            <option key={o.value || 'root'} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <label htmlFor="intent-label">Etykieta kafelka</label>
        <input
          id="intent-label"
          type="text"
          value={intentLabel}
          onChange={(e) => setIntentLabel(e.target.value)}
          placeholder="np. Harmonogram wywozu"
          disabled={!intentTableReady}
        />
        <label htmlFor="intent-kw">Słowa kluczowe (opcjonalnie, po przecinku)</label>
        <input
          id="intent-kw"
          type="text"
          value={intentKeywords}
          onChange={(e) => setIntentKeywords(e.target.value)}
          placeholder="śmieci, odpady, wywóz, harmonogram"
          disabled={!intentTableReady}
        />
        <label htmlFor="intent-canned">Gotowa odpowiedź po wybraniu tego kafelka (tylko liść — bez podtematów)</label>
        <textarea
          id="intent-canned"
          className="welcome"
          rows={5}
          value={intentCanned}
          onChange={(e) => setIntentCanned(e.target.value)}
          placeholder="Opcjonalnie: treść pokazywana od razu użytkownikowi po kliknięciu w ten kafelek (np. link do harmonogramu, krótka instrukcja). Puste = odpowiedź z BIP przez AI."
          disabled={!intentTableReady}
        />
        <label htmlFor="intent-sort">Kolejność (sort_order)</label>
        <input
          id="intent-sort"
          type="number"
          value={intentSort}
          onChange={(e) => setIntentSort(Number(e.target.value))}
          style={{ maxWidth: 120 }}
          disabled={!intentTableReady}
        />
        <label className="chat-assistant-active-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={intentActive}
            onChange={(e) => setIntentActive(e.target.checked)}
            disabled={!intentTableReady}
          />
          Aktywny (widoczny w aplikacji)
        </label>
        <div className="btn-row">
          <button type="button" className="primary" onClick={saveIntent} disabled={savingIntent || !intentTableReady}>
            {savingIntent ? 'Zapisywanie…' : editingIntent ? 'Zapisz węzeł' : 'Dodaj węzeł'}
          </button>
          {editingIntent && (
            <button type="button" className="secondary" onClick={resetIntentForm}>
              Anuluj edycję
            </button>
          )}
        </div>

        <h3 style={{ marginTop: 20 }}>Lista węzłów ({intentNodes.length})</h3>
        {intentNodes.length === 0 ? (
          <p style={{ color: '#6b7280' }}>Brak węzłów — dodaj pierwszy korzeń powyżej.</p>
        ) : (
          <div className="faq-table-wrap">
            <table className="faq-table" aria-label="Drzewo tematów">
              <thead>
                <tr>
                  <th>Etykieta / hierarchia</th>
                  <th>Słowa kluczowe</th>
                  <th>Gotowa odp.</th>
                  <th className="faq-table-col-actions">Akcje</th>
                </tr>
              </thead>
              <tbody>
                {flattenIntentNodesForTable(intentNodes).map(({ node: row, depth }) => (
                  <tr key={row.id}>
                    <td className="faq-table-cell-body">
                      <strong>
                        {'\u00A0\u00A0'.repeat(Math.min(depth, 8))}
                        {depth ? '↳ ' : ''}
                        {row.label}
                      </strong>
                      <div className="faq-meta">
                        kolejność: {row.sort_order ?? 0} · {row.is_active ? 'aktywny' : 'nieaktywny'} · ID: {row.id}
                      </div>
                    </td>
                    <td style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{row.retrieval_keywords || '—'}</td>
                    <td style={{ fontSize: 13, color: row.canned_response ? '#166534' : '#9ca3af', maxWidth: 220 }}>
                      {row.canned_response
                        ? `${String(row.canned_response).slice(0, 80)}${String(row.canned_response).length > 80 ? '…' : ''}`
                        : '—'}
                    </td>
                    <td className="faq-table-cell-actions">
                      <div className="btn-row faq-list-actions">
                        <button type="button" className="secondary" onClick={() => startEditIntent(row)}>
                          Edytuj
                        </button>
                        <button type="button" className="danger" onClick={() => deleteIntent(row.id)}>
                          Usuń
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h2>Uczenie bota — feedback (jak w Cursorze / ChatGPT)</h2>
        <p className="help">
          Opisz **własnymi słowami**, co poszło nie tak i czego oczekujesz — tak jak piszesz uwagi do asystenta. Nie musisz
          składać tego w tabele ani oficjalne zdania: model ma **zrozumieć intencję** i stosować ją przy kolejnych
          odpowiedziach. Opcjonalnie dopisz skrót pytania mieszkańca i/lub złą odpowiedź bota — ułatwia kontekst.
        </p>
        {!feedbackTableReady && (
          <div className="chat-assistant-migration-alert" role="alert">
            <strong>Brak tabeli feedbacku.</strong> Wykonaj migrację{' '}
            <code>supabase/migrations/20260430160000_chat_assistant_feedback.sql</code> w Supabase SQL Editor, potem
            odśwież stronę.
          </div>
        )}
        <label htmlFor="fb-note">Twoja uwaga (wymagane)</label>
        <textarea
          id="fb-note"
          className="welcome"
          rows={6}
          value={fbAdminNote}
          onChange={(e) => setFbAdminNote(e.target.value)}
          placeholder={
            'Np. „Przy pytaniach o skarbnika nie wolno zmieniać imienia Joanna na Janina — zawsze sprawdzaj z aktualnym BIP. Przy sołtysach nie wymyślaj odmian nazw wsi, używaj form z listy.”'
          }
          disabled={!feedbackTableReady}
        />
        <label htmlFor="fb-uq">Skrót pytania mieszkańca (opcjonalnie)</label>
        <input
          id="fb-uq"
          type="text"
          value={fbUserQuestion}
          onChange={(e) => setFbUserQuestion(e.target.value)}
          placeholder='np. „Kto jest skarbnikiem?”'
          disabled={!feedbackTableReady}
        />
        <label htmlFor="fb-wa">Skrót złej odpowiedzi bota (opcjonalnie)</label>
        <input
          id="fb-wa"
          type="text"
          value={fbWrongAnswer}
          onChange={(e) => setFbWrongAnswer(e.target.value)}
          placeholder="Wklej fragment lub jedno zdanie — co było nie tak"
          disabled={!feedbackTableReady}
        />
        <label className="chat-assistant-active-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={fbActive}
            onChange={(e) => setFbActive(e.target.checked)}
            disabled={!feedbackTableReady}
          />
          Aktywna (nieaktywne wpisy nie trafiają do modelu)
        </label>
        <div className="btn-row">
          <button
            type="button"
            className="primary"
            onClick={saveFeedback}
            disabled={savingFeedback || !feedbackTableReady}
          >
            {savingFeedback ? 'Zapisywanie…' : editingFeedback ? 'Zapisz uwagę' : 'Dodaj uwagę'}
          </button>
          {editingFeedback && (
            <button type="button" className="secondary" onClick={resetFeedbackForm}>
              Anuluj edycję
            </button>
          )}
        </div>
        <h3 style={{ marginTop: 20 }}>Historia uwag ({feedbackEntries.length})</h3>
        {feedbackEntries.length === 0 ? (
          <p style={{ color: '#6b7280' }}>Brak wpisów.</p>
        ) : (
          <div className="faq-table-wrap">
            <table className="faq-table" aria-label="Feedback Bappci">
              <thead>
                <tr>
                  <th>Data / uwaga</th>
                  <th className="faq-table-col-actions">Akcje</th>
                </tr>
              </thead>
              <tbody>
                {feedbackEntries.map((f) => (
                  <tr key={f.id}>
                    <td className="faq-table-cell-body">
                      <div className="faq-meta">
                        {f.created_at ? String(f.created_at).slice(0, 10) : ''} · {f.is_active ? 'aktywna' : 'wyłączona'}
                      </div>
                      <div style={{ marginTop: 8, whiteSpace: 'pre-wrap', fontSize: 14 }}>{f.admin_note}</div>
                      {f.user_question_excerpt && (
                        <div style={{ marginTop: 6, fontSize: 13, color: '#4b5563' }}>
                          <em>Pytanie:</em> {f.user_question_excerpt}
                        </div>
                      )}
                      {f.wrong_answer_excerpt && (
                        <div style={{ marginTop: 4, fontSize: 13, color: '#991b1b' }}>
                          <em>Zła odpowiedź:</em> {f.wrong_answer_excerpt}
                        </div>
                      )}
                    </td>
                    <td className="faq-table-cell-actions">
                      <div className="btn-row faq-list-actions">
                        <button type="button" className="secondary" onClick={() => startEditFeedback(f)}>
                          Edytuj
                        </button>
                        <button type="button" className="danger" onClick={() => deleteFeedback(f.id)}>
                          Usuń
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h2>Krótkie korekty faktów (opcjonalnie)</h2>
        <p className="help">
          Jeśli wolisz **jedno zdanie z twardym faktem** (np. urzędowe imię i nazwisko), możesz użyć tej listy zamiast
          długiego feedbacku wyżej. Sekcja **Uwagi** ma szerszy opis (jak w Cursorze); **korekty** to szybkie wpisy
          merytoryczne.
        </p>
        {!correctionsTableReady && (
          <div className="chat-assistant-migration-alert" role="alert">
            <strong>Brak tabeli korekt.</strong> Wykonaj migrację{' '}
            <code>supabase/migrations/20260430140000_chat_assistant_corrections.sql</code> w Supabase SQL Editor, potem
            odśwież stronę.
          </div>
        )}
        <label htmlFor="corr-title">Tytuł (opcjonalnie)</label>
        <input
          id="corr-title"
          type="text"
          value={correctionTitle}
          onChange={(e) => setCorrectionTitle(e.target.value)}
          placeholder="np. Skarbnik"
          disabled={!correctionsTableReady}
        />
        <label htmlFor="corr-body">Treść korekty (wymagane)</label>
        <textarea
          id="corr-body"
          className="welcome"
          rows={4}
          value={correctionBody}
          onChange={(e) => setCorrectionBody(e.target.value)}
          placeholder="Jednoznaczny, prawdziwy opis — imiona i nazwiska dokładnie tak, jak w urzędzie."
          disabled={!correctionsTableReady}
        />
        <label htmlFor="corr-sort">Kolejność</label>
        <input
          id="corr-sort"
          type="number"
          value={correctionSort}
          onChange={(e) => setCorrectionSort(Number(e.target.value))}
          style={{ maxWidth: 120 }}
          disabled={!correctionsTableReady}
        />
        <label className="chat-assistant-active-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={correctionActive}
            onChange={(e) => setCorrectionActive(e.target.checked)}
            disabled={!correctionsTableReady}
          />
          Aktywna
        </label>
        <div className="btn-row">
          <button
            type="button"
            className="primary"
            onClick={saveCorrection}
            disabled={savingCorrection || !correctionsTableReady}
          >
            {savingCorrection ? 'Zapisywanie…' : editingCorrection ? 'Zapisz korektę' : 'Dodaj korektę'}
          </button>
          {editingCorrection && (
            <button type="button" className="secondary" onClick={resetCorrectionForm}>
              Anuluj edycję
            </button>
          )}
        </div>
        <h3 style={{ marginTop: 20 }}>Lista korekt ({corrections.length})</h3>
        {corrections.length === 0 ? (
          <p style={{ color: '#6b7280' }}>Brak wpisów.</p>
        ) : (
          <div className="faq-table-wrap">
            <table className="faq-table" aria-label="Korekty czatu">
              <thead>
                <tr>
                  <th>Tytuł / treść</th>
                  <th className="faq-table-col-actions">Akcje</th>
                </tr>
              </thead>
              <tbody>
                {corrections.map((c) => (
                  <tr key={c.id}>
                    <td className="faq-table-cell-body">
                      {c.title && <strong>{c.title}</strong>}
                      <div style={{ marginTop: 6, whiteSpace: 'pre-wrap', fontSize: 14 }}>{c.body}</div>
                      <div className="faq-meta">
                        kolejność: {c.sort_order ?? 0} · {c.is_active ? 'aktywna' : 'wyłączona'}
                      </div>
                    </td>
                    <td className="faq-table-cell-actions">
                      <div className="btn-row faq-list-actions">
                        <button type="button" className="secondary" onClick={() => startEditCorrection(c)}>
                          Edytuj
                        </button>
                        <button type="button" className="danger" onClick={() => deleteCorrection(c.id)}>
                          Usuń
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h2>Indeks BIP — chat_bot_settings</h2>
        <p className="help">
          W bazie jest jeden wiersz ustawień (<code>id = 1</code>). Pole <strong>Adres bazowy BIP</strong>{' '}
          (<code>crawl_base_url</code>) powinno być takie samo jak przy skanie Playwright, np.{' '}
          <code>https://bip.twoja-gmina.pl/</code>. Dzięki temu asystent dobiera fragmenty z{' '}
          <code>chat_knowledge_chunks</code> z właściwej domeny. Po uruchomieniu{' '}
          <code>npm run crawl</code> skrypt zwykle aktualizuje ten adres i limit stron.
        </p>
        <label htmlFor="crawl-base-url">Adres bazowy BIP (crawl_base_url)</label>
        <input
          id="crawl-base-url"
          type="url"
          className="url-input"
          placeholder="https://bip.przyklad.pl/"
          value={crawlBaseUrl}
          onChange={(e) => setCrawlBaseUrl(e.target.value)}
        />
        <label htmlFor="max-pages">Limit stron skanu (max_pages)</label>
        <input
          id="max-pages"
          type="number"
          min={1}
          max={2000}
          value={maxPages}
          onChange={(e) => setMaxPages(Number(e.target.value))}
          style={{ maxWidth: 120 }}
        />
        <div className="btn-row">
          <button type="button" className="primary" onClick={saveCrawlSettings} disabled={savingCrawlSettings}>
            {savingCrawlSettings ? 'Zapisywanie…' : 'Zapisz ustawienia indeksu'}
          </button>
        </div>
      </section>

      <section>
        <h2>Tekst powitalny</h2>
        <p className="help">
          Pierwsza wiadomość, którą użytkownik widzi po otwarciu czatu w aplikacji mobilnej. Możesz np. krótko
          wyjaśnić, z jakich źródeł korzysta asystent i że nie zastępuje kontaktu z urzędem.
        </p>
        <label htmlFor="welcome-msg">Treść powitalna</label>
        <textarea
          id="welcome-msg"
          className="welcome"
          value={welcomeMessage}
          onChange={(e) => setWelcomeMessage(e.target.value)}
        />
        <div className="btn-row">
          <button type="button" className="primary" onClick={saveWelcome} disabled={savingWelcome}>
            {savingWelcome ? 'Zapisywanie…' : 'Zapisz powitanie'}
          </button>
        </div>
      </section>

      <section>
        <h2>{editing ? 'Edycja FAQ' : 'Nowy wpis FAQ'}</h2>
        <p className="help">
          <strong>Kolejność (pole poniżej):</strong> mniejsza liczba = wyżej na liście w tym panelu i{' '}
          <strong>taka sama kolejność jest używana przy wysyłaniu FAQ do AI</strong> (model dostaje pytania
          odpowiedzi w kolejności rosnącej według tego numeru). Użyj np. 0, 10, 20 — wtedy łatwo wstawić nowy
          wpis „pomiędzy” bez przenumerowywania wszystkich.
        </p>
        <label>Pytanie</label>
        <input type="text" value={formQ} onChange={(e) => setFormQ(e.target.value)} />
        <label>Odpowiedź</label>
        <textarea className="faq" value={formA} onChange={(e) => setFormA(e.target.value)} />
        <label>Kolejność (sort_order)</label>
        <input
          type="number"
          value={formOrder}
          onChange={(e) => setFormOrder(Number(e.target.value))}
          style={{ maxWidth: 120 }}
        />
        <h3 className="chat-assistant-faq-visibility">W aplikacji mobilnej</h3>
        <label className="chat-assistant-active-label" style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, marginTop: 4 }}>
          <input type="checkbox" checked={formActive} onChange={(e) => setFormActive(e.target.checked)} />
          Aktywny (widoczny w aplikacji i w kontekście bota)
        </label>
        <p className="help" style={{ marginTop: 8 }}>
          <strong>Kafelki szybkich pytań</strong> ustawiasz w tabeli poniżej: <strong>niebieskim kwadratem</strong> w
          pierwszej kolumnie (max {MAX_PINNED_FAQ}, tylko <strong>aktywne</strong> pytania). Kolejność kafelków = kolejność
          przypięć.
        </p>
        <div className="btn-row">
          <button type="button" className="primary" onClick={saveFaq}>
            Zapisz
          </button>
          {editing && (
            <button type="button" className="secondary" onClick={resetForm}>
              Anuluj edycję
            </button>
          )}
        </div>
      </section>

      <section>
        <h2>Lista FAQ ({faqs.length})</h2>
        <p className="chat-assistant-pin-hint">
          <strong>Kafelki w czacie mobilnym:</strong> w pierwszej kolumnie tabeli kliknij <strong>niebieski kwadrat</strong>{' '}
          (✓ = przypięte). Max {MAX_PINNED_FAQ}, tylko <em>aktywne</em> wpisy. Zapis:{' '}
          <code>chat_bot_settings.pinned_faq_ids</code>.
        </p>
        <p style={{ fontSize: '0.875rem', color: '#4b5563', marginTop: '-0.25rem', marginBottom: '0.75rem' }}>
          Przypiętych kafelków: <strong>{pinnedCount}</strong> / {MAX_PINNED_FAQ}
        </p>
        {faqs.length === 0 ? (
          <p style={{ color: '#6b7280' }}>Brak wpisów — dodaj pierwszy powyżej.</p>
        ) : (
          <>
          <div className="faq-pin-legend" role="note">
            W pierwszej kolumnie tabeli jest <strong>niebieski kwadrat</strong> — kliknij go, żeby przypiąć lub odpiąć
            pytanie jako kafelek w czacie mobilnym (max {MAX_PINNED_FAQ}, tylko aktywne wpisy).
          </div>
          <div className="faq-table-wrap">
            <table className="faq-table" aria-label="Lista FAQ z opcją kafelka w czacie">
              <thead>
                <tr>
                  <th scope="col" className="faq-table-col-pin">
                    Kafelek — kliknij niebieski kwadrat
                  </th>
                  <th scope="col">Pytanie i odpowiedź</th>
                  <th scope="col" className="faq-table-col-actions">
                    Akcje
                  </th>
                </tr>
              </thead>
              <tbody>
                {faqs.map((f) => {
                  const isPinned = pinnedFaqIds.includes(String(f.id));
                  const pinDisabled =
                    !pinnedFaqDbReady ||
                    pinSavingId != null ||
                    (!f.is_active && !isPinned);
                  const pinTitle = !pinnedFaqDbReady
                    ? 'Dodaj kolumnę pinned_faq_ids (migracja SQL)'
                    : !f.is_active && !isPinned
                      ? 'Aktywuj wpis, żeby zaznaczyć kafelek'
                      : 'Pokaż to pytanie jako kafelek pod powitaniem w aplikacji mobilnej';
                  return (
                    <tr key={f.id}>
                      <td className="faq-table-cell-pin">
                        <div className="faq-pin-cell-wrap">
                          <button
                            type="button"
                            className={`faq-pin-toggle${isPinned ? ' faq-pin-toggle--on' : ''}`}
                            disabled={pinDisabled}
                            onClick={() => setFaqPinned(f, !isPinned)}
                            title={pinTitle}
                            aria-label={
                              isPinned
                                ? `Odepnij kafelek: ${f.question}`
                                : `Przypnij jako kafelek: ${f.question}`
                            }
                            aria-pressed={isPinned}
                          >
                            {isPinned ? '✓' : '\u00a0'}
                          </button>
                          <span className="faq-pin-state-text">{isPinned ? 'Tak' : 'Nie'}</span>
                        </div>
                      </td>
                      <td className="faq-table-cell-body">
                        <strong>{f.question}</strong>
                        {!f.is_active && (
                          <span style={{ marginLeft: 8, fontSize: 12, color: '#9ca3af' }}>(nieaktywne)</span>
                        )}
                        <div className="faq-meta">
                          Kolejność: {f.sort_order ?? 0} · ID: {f.id}
                        </div>
                        <div style={{ marginTop: 8, whiteSpace: 'pre-wrap', fontSize: 14 }}>{f.answer}</div>
                      </td>
                      <td className="faq-table-cell-actions">
                        <div className="btn-row faq-list-actions">
                          <button type="button" className="secondary" onClick={() => startEdit(f)}>
                            Edytuj
                          </button>
                          <button type="button" className="danger" onClick={() => deleteFaq(f.id)}>
                            Usuń
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </section>
    </div>
  );
}
